<template>
    <div class="evaluation-booking-page">
        <v-container class="fill-height d-flex align-center justify-center">
            <v-card flat class="booking-card" max-width="680">

                <!-- Success State (just confirmed booking) -->
                <template v-if="booked">
                    <div class="success-state">
                        <div class="success-icon-wrapper mb-8">
                            <div class="success-circle">
                                <v-icon size="48" color="#FFFFFF">mdi-check</v-icon>
                            </div>
                        </div>

                        <h2 class="success-title mb-3">Consultation Booked!</h2>
                        <p class="success-subtitle mb-8">
                            Your consultation session has been scheduled. We'll send you the meeting details via email before your session.
                        </p>

                        <div class="d-flex flex-column align-center">
                            <div class="booking-details mb-6">
                                <div class="detail-row" v-if="bookedDateFormatted">
                                    <v-icon size="20" color="#D4780A">mdi-calendar</v-icon>
                                    <span>{{ bookedDateFormatted }}</span>
                                </div>
                                <div class="detail-row" v-if="bookedTimeFormatted">
                                    <v-icon size="20" color="#D4780A">mdi-clock-outline</v-icon>
                                    <span>{{ bookedTimeFormatted }}</span>
                                </div>
                            </div>

                            <v-btn
                                depressed
                                x-large
                                color="#D4780A"
                                class="start-btn white--text mb-4"
                                @click="goToCourses"
                            >
                                Browse Courses
                            </v-btn>

                            <button class="text-link" @click="goToHome">
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                </template>

                <!-- Already Booked State (pre-existing consultation) -->
                <template v-else-if="alreadyBooked">
                    <div class="success-state">
                        <div class="success-icon-wrapper mb-8">
                            <div class="success-circle">
                                <v-icon size="48" color="#FFFFFF">mdi-check</v-icon>
                            </div>
                        </div>

                        <h2 class="success-title mb-3">Session Already Booked</h2>
                        <p class="success-subtitle mb-8">
                            You already have a consultation session scheduled. We'll send you the meeting details via email before your session.
                        </p>

                        <div class="d-flex flex-column align-center">
                            <div class="booking-details mb-6">
                                <div class="detail-row" v-if="existingDateFormatted">
                                    <v-icon size="20" color="#D4780A">mdi-calendar</v-icon>
                                    <span>{{ existingDateFormatted }}</span>
                                </div>
                                <div class="detail-row" v-if="existingTimeFormatted">
                                    <v-icon size="20" color="#D4780A">mdi-clock-outline</v-icon>
                                    <span>{{ existingTimeFormatted }}</span>
                                </div>
                            </div>

                            <v-btn
                                depressed
                                x-large
                                color="#D4780A"
                                class="start-btn white--text mb-4"
                                @click="goToCourses"
                            >
                                Browse Courses
                            </v-btn>

                            <button class="text-link" @click="goToHome">
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                </template>

                <!-- Default: Booking State (show calendar + topic) -->
                <template v-else>
                    <div class="card-header">
                        <div class="header-icon">
                            <v-icon size="36" color="#D4780A">mdi-message-text-outline</v-icon>
                        </div>
                        <h1 class="main-title">Book Your Consultation Session</h1>
                        <p class="main-subtitle">
                            A focused session with our team to discuss your specific question or topic.
                        </p>
                    </div>

                    <div class="card-body">
                        <evaluation-calendar
                            v-model="selectedSlot"
                            :whatsapp-number="whatsappNumber"
                            @slot-selected="onSlotSelected"
                        />

                        <!-- Topic textarea (mandatory) -->
                        <div v-if="selectedSlot" class="mt-6">
                            <label class="topic-label">
                                What would you like to discuss?
                                <span class="required-star">*</span>
                            </label>
                            <v-textarea
                                v-model="topic"
                                outlined
                                rows="3"
                                auto-grow
                                hide-details="auto"
                                counter="500"
                                placeholder="e.g. understanding tajweed rules for a specific surah, planning a long-term memorization schedule, etc."
                                :rules="topicRules"
                                :error="topicAttempted && !topicValid"
                                :error-messages="topicAttempted && !topicValid ? ['Please describe your topic (at least 5 characters).'] : []"
                                @blur="topicAttempted = true"
                            />
                        </div>

                        <!-- Confirm Button -->
                        <v-btn
                            v-if="selectedSlot"
                            block
                            x-large
                            depressed
                            color="#D4780A"
                            class="confirm-btn white--text mt-6"
                            :loading="booking"
                            :disabled="!canBook"
                            @click="confirmBooking"
                        >
                            Confirm Booking
                        </v-btn>
                    </div>
                </template>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import moment from 'moment-timezone';
import EvaluationCalendar from '@/components/custom/EvaluationCalendar.vue';
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';

export default {
    name: 'ConsultationBooking',
    components: {
        EvaluationCalendar
    },
    data() {
        return {
            selectedSlot: '',
            slotDetails: null,
            topic: '',
            topicAttempted: false,
            booking: false,
            booked: false,
            bookedData: null,
            // Already-booked guard
            alreadyBooked: false,
            existing: null,
            checkingExisting: false,
            topicRules: [
                v => (v && v.trim().length >= 5) || 'Please describe your topic (at least 5 characters).',
                v => (v && v.trim().length <= 500) || 'Topic must be 500 characters or fewer.',
            ],
        };
    },
    computed: {
        whatsappNumber() {
            return this.appData?.whatsapp_number || '';
        },
        topicValid() {
            return typeof this.topic === 'string'
                && this.topic.trim().length >= 5
                && this.topic.trim().length <= 500;
        },
        canBook() {
            return !!this.selectedSlot && this.topicValid && !this.booking;
        },
        bookedDateFormatted() {
            if (!this.bookedData) return '';
            const m = moment.utc(this.bookedData.booked_start).tz(moment.tz.guess());
            return m.format('dddd, MMMM D, YYYY');
        },
        bookedTimeFormatted() {
            if (!this.bookedData) return '';
            const tz = moment.tz.guess();
            const m = moment.utc(this.bookedData.booked_start).tz(tz);
            const end = moment.utc(this.bookedData.booked_end).tz(tz);
            return `${m.format('h:mm A')} – ${end.format('h:mm A')}`;
        },
        existingDateFormatted() {
            if (!this.existing) return '';
            const m = moment.utc(this.existing.booked_start).tz(moment.tz.guess());
            return m.format('dddd, MMMM D, YYYY');
        },
        existingTimeFormatted() {
            if (!this.existing) return '';
            const tz = moment.tz.guess();
            const m = moment.utc(this.existing.booked_start).tz(tz);
            const end = moment.utc(this.existing.booked_end).tz(tz);
            return `${m.format('h:mm A')} – ${end.format('h:mm A')}`;
        },
        ...GlobalComputed
    },
    methods: {
        onSlotSelected(details) {
            this.slotDetails = details;
        },
        async checkExistingConsultation() {
            this.checkingExisting = true;
            try {
                const response = await window.axios.get('/evaluations/my-consultation');
                if (response.data.has_active_consultation || response.data.has_completed_consultation) {
                    // Already booked or already attended — redirect to home
                    this.$router.replace({ name: 'home' });
                }
            } catch (e) {
                // silently ignore
            } finally {
                this.checkingExisting = false;
            }
        },
        async confirmBooking() {
            this.topicAttempted = true;
            if (!this.canBook) return;

            this.booking = true;
            try {
                const response = await window.axios.post('/evaluations/book', {
                    booked_start: this.selectedSlot,
                    type: 'consultation',
                    topic: this.topic.trim(),
                });
                this.bookedData = response.data;
                this.booked = true;

                // Refresh user to clear needs_consultation_redirect
                this.$store.dispatch('refreshUser');
            } catch (error) {
                const msg = error.response?.data?.message
                    || (error.response?.data?.errors ? Object.values(error.response.data.errors).flat().join(' ') : null)
                    || 'Failed to book consultation. Please try again.';
                this.showMsg(msg, 'error');
            } finally {
                this.booking = false;
            }
        },
        goToCourses() {
            this.$router.push({ name: 'courses' });
        },
        goToHome() {
            this.$router.push({ name: 'home' });
        },
        ...GlobalMethods
    },
    mounted() {
        // Redirect away if consultation booking is not required at all
        if (this.user && this.user.consultation_required === false) {
            this.$router.push({ name: 'home' });
            return;
        }
        this.checkExistingConsultation();
    }
};
</script>

<style lang="scss" scoped>
.evaluation-booking-page {
    min-height: 100vh;
    background-color: #FAFAFA;
    padding: 20px 0;

    @media (max-width: 960px) {
        padding: 0;
        background-color: white;
    }
}

.booking-card {
    width: 100%;
    padding: 48px 40px;
    background: white;
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);

    @media (max-width: 960px) {
        padding: 24px 16px 80px;
        border-radius: 0;
        box-shadow: none;
    }
}

.card-header {
    text-align: center;
    margin-bottom: 36px;
}

.header-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: #FFF3E0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
}

.main-title {
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1E1E1E;
    line-height: 1.3;
    margin-bottom: 8px;

    @media (max-width: 600px) {
        font-size: 24px;
    }
}

.main-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #707070;
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto;
}

.card-body {
    max-width: 520px;
    margin: 0 auto;
}

.topic-label {
    display: block;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #1E1E1E;
    margin-bottom: 8px;
}

.required-star {
    color: #D4780A;
    font-weight: 700;
}

.confirm-btn {
    height: 52px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
}

.success-state {
    text-align: center;
    padding: 40px 0;

    @media (max-width: 600px) {
        padding: 20px 0;
    }
}

.success-icon-wrapper {
    display: flex;
    justify-content: center;
}

.success-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #D4780A, #E79007);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(212, 120, 10, 0.3);
}

.success-title {
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1E1E1E;

    @media (max-width: 600px) {
        font-size: 24px;
    }
}

.success-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #707070;
    line-height: 1.6;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
}

.booking-details {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: #FFF8F0;
    border: 1px solid #FFE0B2;
    border-radius: 12px;
    padding: 20px 32px;
}

.detail-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #1E1E1E;
}

.start-btn {
    height: 52px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
    padding: 0 40px !important;

    @media (max-width: 600px) {
        width: 100%;
    }
}

.text-link {
    display: block;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #9E9E9E;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: #707070;
    }
}
</style>
